import { useEffect, useState, useMemo, useCallback, memo } from "react";
import { useTranslation } from "react-i18next";
import {
	IconButton,
	Paper,
	Tooltip,
	Typography,
	Button,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	Checkbox,
	Dialog,
	DialogContent,
	DialogTitle,
	DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
// cmps
import Svg from "../svg";
import Toast from "../Toast";
import Na from "../na";
// hooks
import useSend from "../../hooks/useSend";
// services
import User from "../../services/user";
// types
import type { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import type { GatewayJsonData, GroupName, FilteredGroups } from "../../types/firmware-groups";
import type { GatewayId } from "../../types/gateway";
import type { CmdGetGroupGateways, CmdMoveGatewayToOtherGroup } from "../../types/gupport";

type Props = Readonly<{
	selectedGroupName: GroupName;
	groups: FilteredGroups;
}>;

const GroupGateways = ({ selectedGroupName, groups }: Props) => {
	const send = useSend();

	const { t } = useTranslation();

	const [loading, setLoading] = useState(true);
	const [gateways, setGateways] = useState<ReadonlyArray<GatewayJsonData>>([]);
	const [selectedGatewayIds, setSelectedGatewayIds] = useState<ReadonlyArray<GatewayId>>([]);
	const [targetGroupName, setTargetGroupName] = useState("");
	const [inProgress, setInProgress] = useState(false);
	const [showMoveGatewayDialog, setShowMoveGatewayDialog] = useState(false);
	const [showMoveGatewaySuccess, setShowMoveGatewaySuccess] = useState(false);
	const [showGenericErrorMsg, setShowGenericErrorMsg] = useState<number | undefined>(undefined);

	const fetchGroupGateways = useCallback(() => {
		setLoading(true);
		const cmd = {
			action: "getGroupsGateways",
			group: selectedGroupName,
		} as const satisfies CmdGetGroupGateways;
		send(cmd, (error, msg) => {
			if (!error && msg?.payload.status === "ok") {
				setShowGenericErrorMsg(undefined);
				setGateways(msg.payload.data);
			} else {
				setShowGenericErrorMsg(Date.now());
			}
			setLoading(false);
		});
	}, [selectedGroupName, send]);

	useEffect(() => {
		fetchGroupGateways();
	}, [fetchGroupGateways]);

	const columns = useMemo(() => ([
		{
			field: "id",
			headerName: t("firmwareGroups.id"),
			flex: 3,
		},
		{
			field: "code",
			headerName: t("firmwareGroups.code"),
			flex: 2,
		},
		{
			field: "group",
			headerName: t("firmwareGroups.group"),
			flex: 2,
			valueGetter: () => (selectedGroupName),
		},
		{
			field: "pd",
			headerName: t("firmwareGroups.pd"),
			flex: 2,
			renderCell: (params) => (params.row.pd ?? <Na />),
		},
		{
			field: "po",
			headerName: t("firmwareGroups.order"),
			flex: 2,
			renderCell: (params) => (params.row.po ?? <Na />),
		},
		{
			field: "sku",
			headerName: t("firmwareGroups.sku"),
			flex: 2,
			renderCell: (params) => (params.row.sku ?? <Na />),
		},
	] as const satisfies ReadonlyArray<GridColDef<GatewayJsonData>>), [selectedGroupName, t]);

	const handleRowSelectionModelChange = useCallback((model: GridRowSelectionModel) => {
		if (model.type === "include") {
			setSelectedGatewayIds(Array.from(model.ids) as Array<GatewayId>);
		} else {
			setSelectedGatewayIds(gateways.map((gateway) => gateway.id).filter((id) => (!model.ids.has(id))));
		}
	}, [gateways]);

	const handleDialogClose = () => {
		setShowMoveGatewayDialog(false);
		setTargetGroupName("");
	};

	const handleMoveGateway = () => {
		setInProgress(true);
		const cmd = {
			action: "setGatewayGroup",
			group: targetGroupName as GroupName,
			gateway: selectedGatewayIds,
		} as const satisfies CmdMoveGatewayToOtherGroup;
		send(cmd, (error, msg) => {
			if (!error && msg?.payload.status === "ok") {
				setShowGenericErrorMsg(undefined);
				setShowMoveGatewaySuccess(true);
				fetchGroupGateways();
			} else {
				setShowGenericErrorMsg(Date.now());
			}
			setInProgress(false);
			setShowMoveGatewayDialog(false);
			setTargetGroupName("");
		});
	};

	const allGroups = groups.flatMap((group) => (group.names)).filter((name) => name !== selectedGroupName);

	return (
		<>
			<Typography variant="h5" gutterBottom={true}>{t("firmwareGroups.gateways")}</Typography>
			<Paper sx={{ p: 1 }}>
				<DataGrid
					rows={gateways}
					columns={columns}
					onRowSelectionModelChange={handleRowSelectionModelChange}
					loading={loading}
					sx={{
						"backgroundColor": "inherit",
						"& .MuiDataGrid-row:hover": {
							cursor: "pointer",
						},
					}}
				/>
			</Paper>
			{User.hasLevel("gw_write") &&
				<div style={{ marginTop: "8px" }}>
					<Tooltip title={t("firmwareGroups.actions.moveGateway.tooltipTitle")}>
						<span>
							<IconButton
								disabled={selectedGatewayIds.length === 0}
								onClick={() => (setShowMoveGatewayDialog(true))}
								aria-label={t("firmwareGroups.actions.moveGateway.tooltipTitle")}
							>
								<Svg src="navigation/arrowRight.svg" />
							</IconButton>
						</span>
					</Tooltip>
				</div>
			}
			<Dialog
				fullWidth={true}
				open={showMoveGatewayDialog}
				onClose={handleDialogClose}
			>
				<DialogTitle>{t("firmwareGroups.actions.moveGateway.dialogTitle")}</DialogTitle>
				<DialogContent>
					<List>
						{allGroups.length === 0 ?
							<Typography sx={{ padding: "1em", textAlign: "center" }}>
								{t("firmwareGroups.actions.moveGateway.noGroups")}
							</Typography>
							: allGroups.map((groupName) => (
								<ListItem
									key={groupName}
									disablePadding={true}
									onClick={() => setTargetGroupName(groupName)}
								>
									<ListItemIcon>
										<Checkbox edge="start" checked={groupName === targetGroupName} />
									</ListItemIcon>
									<ListItemText primary={groupName} />
								</ListItem>
							))}
					</List>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleDialogClose}>{t("dialog.cancel")}</Button>
					<Button
						disabled={targetGroupName === ""}
						loading={inProgress}
						onClick={handleMoveGateway}
					>
						{t("dialog.confirm")}
					</Button>
				</DialogActions>
			</Dialog>
			<Toast
				autoHideDuration={3000}
				severity="success"
				open={showMoveGatewaySuccess}
				onClose={setShowMoveGatewaySuccess}
				message={t("firmwareGroups.actions.moveGateway.successMsg")}
			/>
			<Toast
				autoHideDuration={6000}
				severity="error"
				open={showGenericErrorMsg}
				onClose={setShowGenericErrorMsg}
				message={t("toast.genericErrorMsg")}
			/>
		</>
	);
};

export default memo(GroupGateways);
